/**
 * Typed access to the Electron preload bridge. Everything here degrades to a
 * no-op in the browser, so pages can call it unconditionally.
 */

export interface DesktopContext {
  apiBaseUrl: string;
  appVersion: string;
  platform: NodeJS.Platform | string;
  isDesktop: true;
  screenPermission: 'granted' | 'denied' | 'not-determined' | 'restricted' | 'unknown' | 'stale';
  launchAtLogin: boolean;
  notifyOnCapture: boolean;
  /** An admin has switched capture notifications off for everyone. */
  notifyOnCaptureLocked?: boolean;
  pendingUploads: number;
  webBaseUrl: string;
  /** 'precise' = native input hook active; 'sampled' = idle-timer + cursor polling. */
  activityMode: 'precise' | 'sampled';
  preciseActivityAvailable: boolean;
}

export type UpdateStage = 'idle' | 'checking' | 'available' | 'downloading' | 'ready' | 'none' | 'error';

export interface UpdateState {
  stage: UpdateStage;
  version: string | null;
  percent: number;
  bytesPerSecond: number;
  message: string | null;
  /** The build cannot replace itself (unsigned on macOS); offer a download instead. */
  manualOnly: boolean;
  currentVersion: string;
}

export interface DesktopBridge {
  isDesktop: true;
  apiBaseUrl: string;
  appVersion: string;
  platform: string;
  getContext(): Promise<DesktopContext>;
  setTokens(tokens: { accessToken: string; refreshToken: string } | null): Promise<void>;
  clearTokens(): Promise<void>;
  /**
   * The desktop's only refresh path. Main owns the refresh token; passing the
   * window's current access token lets main hand back an already-fresh pair
   * without a second (family-revoking) refresh.
   */
  refreshTokens(staleAccessToken: string | null): Promise<{ accessToken: string; refreshToken: string } | null>;
  onTokensUpdated(cb: (tokens: { accessToken: string; refreshToken: string }) => void): () => void;
  notifyTimerChanged(): Promise<void>;
  setLaunchAtLogin(enabled: boolean): Promise<void>;
  setNotifyOnCapture(enabled: boolean): Promise<void>;
  requestScreenPermission(): Promise<DesktopContext['screenPermission']>;
  captureNow(): Promise<{ captured: number; skippedReason?: string; reason?: string }>;
  /** Opens a path in the user's real browser — the compact app defers to the web app. */
  openWeb(path: string): Promise<void>;
  /** Tells the main process where the web app lives, learned from /api/auth/me. */
  setWebBaseUrl(url: string): Promise<void>;
  minimizeWindow(): Promise<void>;
  hideWindow(): Promise<void>;
  relaunch(): Promise<void>;
  enablePreciseActivity(): Promise<{ attached: boolean; mode: 'precise' | 'sampled'; permitted: boolean }>;

  getUpdateState(): Promise<UpdateState>;
  checkForUpdates(): Promise<UpdateState>;
  installUpdate(): Promise<boolean>;
  openDownloadPage(): Promise<void>;
  onUpdateState(cb: (state: UpdateState) => void): () => void;
  onPermissionRequired(cb: () => void): () => void;
  onTimerChanged(cb: () => void): () => void;
}

export function desktop(): DesktopBridge | null {
  const bridge = (globalThis as { chronos?: DesktopBridge }).chronos;
  return bridge?.isDesktop ? bridge : null;
}

export const isDesktopApp = (): boolean => desktop() !== null;
